import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Spinner, TextInput } from '@bravemove/ui-web';
import { CompassWordmark } from '@bravemove/design-tokens/brand';
import { adminFetch } from '../auth/apiClient';
import { ApiError, type AcceptInviteResponse, type InvitePreviewResponse } from '../auth/types';
import { translateApiError } from '../auth/errors';
import { AuthLayout } from '../layout/AuthLayout';

interface FormValues {
  readonly password: string;
  readonly confirmPassword: string;
}

const strongPassword = z
  .string()
  .min(12)
  .refine((v) => /[a-z]/.test(v), { message: 'lower' })
  .refine((v) => /[A-Z]/.test(v), { message: 'upper' })
  .refine((v) => /\d/.test(v), { message: 'digit' })
  .refine((v) => /[^A-Za-z0-9]/.test(v), { message: 'symbol' });

export function AcceptInvitePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const params = useParams<{ token: string }>();
  const token = params.token ?? '';
  const [preview, setPreview] = useState<InvitePreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const schema = z
    .object({
      password: strongPassword,
      confirmPassword: z.string(),
    })
    .refine((d) => d.password === d.confirmPassword, {
      message: t('admin:validation.passwordMismatch'),
      path: ['confirmPassword'],
    });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await adminFetch<InvitePreviewResponse>(
          `/admin/auth/invite/${encodeURIComponent(token)}`,
        );
        if (cancelled) return;
        setPreview(data);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 410 || err.code === 'INVITE_EXPIRED')) {
          setExpired(true);
        } else {
          setServerError(translateApiError(err, t));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      const res = await adminFetch<AcceptInviteResponse>('/admin/auth/invite/accept', {
        method: 'POST',
        body: { token, password: values.password },
      });
      navigate('/totp-enroll', {
        state: { enrollmentToken: res.enrollmentToken },
        replace: true,
      });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 410 || err.code === 'INVITE_EXPIRED')) {
        setExpired(true);
        return;
      }
      setServerError(translateApiError(err, t));
    }
  });

  if (loading) {
    return (
      <AuthLayout>
        <div className="flex items-center justify-center py-8">
          <Spinner size={32} />
        </div>
      </AuthLayout>
    );
  }

  if (expired) {
    return (
      <AuthLayout>
        <header className="text-center mb-6">
          <CompassWordmark size={28} />
          <h1 className="text-h2 font-bold text-ink mt-4">
            {t('admin:acceptInvite.expiredTitle')}
          </h1>
          <p className="text-body text-ink-2 mt-1">{t('admin:acceptInvite.expiredBody')}</p>
        </header>
        <Button type="button" variant="primary" fullWidth onClick={() => navigate('/login')}>
          {t('admin:forgotPassword.backToLogin')}
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <header className="text-center mb-6">
        <CompassWordmark size={28} />
        <h1 className="text-h2 font-bold text-ink mt-4">{t('admin:acceptInvite.title')}</h1>
        <p className="text-body text-ink-2 mt-1">{t('admin:acceptInvite.subtitle')}</p>
      </header>
      {preview ? (
        <div className="mb-4 p-4 rounded-xl bg-surface-2 border border-hairline text-[13px] text-ink-2 space-y-1">
          <div>
            <span className="font-mono text-ink-3 uppercase tracking-wider text-[11px]">
              {t('admin:common.email')}
            </span>{' '}
            <span className="text-ink font-semibold">{preview.email}</span>
          </div>
          <div>
            <span className="font-mono text-ink-3 uppercase tracking-wider text-[11px]">
              {t('admin:acceptInvite.invitedAs')}
            </span>{' '}
            <span className="text-ink font-semibold">{t(`admin:roles.${preview.role}`)}</span>
          </div>
        </div>
      ) : null}
      {serverError ? (
        <Banner tone="danger" className="mb-4">
          {serverError}
        </Banner>
      ) : null}
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormField
          label={t('admin:common.password')}
          error={
            form.formState.errors.password ? t('admin:validation.passwordStrength') : undefined
          }
          required
        >
          <TextInput type="password" autoComplete="new-password" {...form.register('password')} />
        </FormField>
        <FormField
          label={t('admin:common.confirmPassword')}
          error={form.formState.errors.confirmPassword?.message}
          required
        >
          <TextInput
            type="password"
            autoComplete="new-password"
            {...form.register('confirmPassword')}
          />
        </FormField>
        <Button type="submit" variant="primary" fullWidth loading={form.formState.isSubmitting}>
          {t('admin:acceptInvite.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}
